/* ============================================
   THE VITAMIN PATCH — shopify-cart.js
   Cart Management via Storefront API
   ============================================
   REQUIRES: shopify-config.js loaded first

   WHAT THIS DOES:
   - Injects a cart drawer into every page
   - Wires your existing .icon-btn-cart button
   - Updates your existing .cart-badge count
   - Add, remove, update cart items
   - Sends customer to Shopify hosted checkout
   - Cart persists across page navigation
   ============================================ */

(function() {
  'use strict';

  var CART_KEY = 'tvp_cart_id';

  function getCartId()   { return localStorage.getItem(CART_KEY); }
  function setCartId(id) { localStorage.setItem(CART_KEY, id); }
  function clearCart()   { localStorage.removeItem(CART_KEY); }

  /* ------------------------------------------
     GRAPHQL MUTATIONS
     ------------------------------------------ */
  var CREATE_CART = '\
    mutation cartCreate($lines: [CartLineInput!]) {\
      cartCreate(input: { lines: $lines }) {\
        cart {\
          id checkoutUrl\
          lines(first: 50) { edges { node {\
            id quantity\
            cost { totalAmount { amount } amountPerQuantity { amount } }\
            merchandise { ... on ProductVariant {\
              id title price { amount }\
              product { title featuredImage { url } }\
            }}\
          }}}\
        }\
        userErrors { field message }\
      }\
    }';

  var ADD_LINES = '\
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {\
      cartLinesAdd(cartId: $cartId, lines: $lines) {\
        cart {\
          id checkoutUrl\
          lines(first: 50) { edges { node {\
            id quantity\
            cost { totalAmount { amount } amountPerQuantity { amount } }\
            merchandise { ... on ProductVariant {\
              id title price { amount }\
              product { title featuredImage { url } }\
            }}\
          }}}\
        }\
        userErrors { field message }\
      }\
    }';

  var UPDATE_LINES = '\
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {\
      cartLinesUpdate(cartId: $cartId, lines: $lines) {\
        cart {\
          id checkoutUrl\
          lines(first: 50) { edges { node {\
            id quantity\
            cost { totalAmount { amount } amountPerQuantity { amount } }\
            merchandise { ... on ProductVariant {\
              id title price { amount }\
              product { title featuredImage { url } }\
            }}\
          }}}\
        }\
      }\
    }';

  var REMOVE_LINES = '\
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {\
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {\
        cart {\
          id checkoutUrl\
          lines(first: 50) { edges { node {\
            id quantity\
            cost { totalAmount { amount } amountPerQuantity { amount } }\
            merchandise { ... on ProductVariant {\
              id title price { amount }\
              product { title featuredImage { url } }\
            }}\
          }}}\
        }\
      }\
    }';

  var GET_CART = '\
    query getCart($cartId: ID!) {\
      cart(id: $cartId) {\
        id checkoutUrl\
        lines(first: 50) { edges { node {\
          id quantity\
          cost { totalAmount { amount } amountPerQuantity { amount } }\
          merchandise { ... on ProductVariant {\
            id title price { amount }\
            product { title featuredImage { url } }\
          }}\
        }}}\
      }\
    }';

  /* ------------------------------------------
     INJECT CART DRAWER INTO PAGE
     ------------------------------------------ */
  function injectDrawer() {
    if (document.getElementById('tvpCartDrawer')) return;

    var html = '\
      <div id="tvpCartOverlay" class="tvp-co"></div>\
      <div id="tvpCartDrawer" class="tvp-cd">\
        <div class="tvp-cd-head">\
          <h3 class="tvp-cd-title">Your Cart</h3>\
          <button id="tvpCartClose" class="tvp-cd-close" aria-label="Close">\
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">\
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>\
            </svg>\
          </button>\
        </div>\
        <div id="tvpCartItems" class="tvp-cd-items">\
          <div class="tvp-cd-empty">\
            <p>Your cart is empty.</p>\
            <a href="/pages/catalog" class="pill-btn pill-btn-sm" style="margin-top:16px;display:inline-block;">SHOP ALL PATCHES</a>\
          </div>\
        </div>\
        <div id="tvpCartFoot" class="tvp-cd-foot" style="display:none;">\
          <div class="tvp-cd-sub"><span>Subtotal</span><span id="tvpSubtotal">$0.00</span></div>\
          <p class="tvp-cd-note">Shipping & taxes calculated at checkout.</p>\
          <button id="tvpCheckout" class="pill-btn" style="width:100%;background:#f97316;border-color:#f97316;color:#000;margin-top:12px;">CHECKOUT</button>\
        </div>\
      </div>';

    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    document.body.appendChild(wrap);

    var css = document.createElement('style');
    css.textContent = [
      '.tvp-co{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:9998;opacity:0;pointer-events:none;transition:opacity .3s}',
      '.tvp-co.open{opacity:1;pointer-events:all}',
      '.tvp-cd{position:fixed;top:0;right:0;width:400px;max-width:100vw;height:100%;background:#fff;z-index:9999;transform:translateX(100%);transition:transform .35s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;box-shadow:-4px 0 24px rgba(0,0,0,.12)}',
      '.tvp-cd.open{transform:translateX(0)}',
      '.tvp-cd-head{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid #eee}',
      '.tvp-cd-title{font-family:"Bebas Neue",sans-serif;font-size:22px;letter-spacing:1px;margin:0}',
      '.tvp-cd-close{background:none;border:none;cursor:pointer;padding:4px;display:flex;align-items:center}',
      '.tvp-cd-items{flex:1;overflow-y:auto;padding:16px 24px}',
      '.tvp-cd-empty{text-align:center;padding:48px 0;font-family:Barlow,sans-serif;color:#666}',
      '.tvp-cd-item{display:flex;gap:14px;padding:14px 0;border-bottom:1px solid #f0f0f0}',
      '.tvp-cd-img{width:72px;height:72px;object-fit:cover;border-radius:6px;flex-shrink:0;background:#f5f5f5}',
      '.tvp-cd-body{flex:1;font-family:Barlow,sans-serif}',
      '.tvp-cd-name{font-weight:600;font-size:14px;margin-bottom:4px;color:#212121}',
      '.tvp-cd-variant{font-size:12px;color:#888;margin-bottom:8px}',
      '.tvp-cd-controls{display:flex;align-items:center;gap:10px}',
      '.tvp-cd-qbtn{width:26px;height:26px;border:1px solid #ddd;background:#fff;border-radius:4px;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;line-height:1}',
      '.tvp-cd-qty{font-size:14px;font-weight:600;min-width:20px;text-align:center;color:#212121}',
      '.tvp-cd-price{font-weight:700;font-size:14px;margin-left:auto;color:#212121}',
      '.tvp-cd-remove{background:none;border:none;color:#aaa;cursor:pointer;font-size:11px;font-family:Barlow,sans-serif;text-decoration:underline;margin-left:8px}',
      '.tvp-cd-foot{padding:16px 24px 28px;border-top:1px solid #eee}',
      '.tvp-cd-sub{display:flex;justify-content:space-between;font-family:Barlow,sans-serif;font-weight:700;font-size:16px;margin-bottom:6px}',
      '.tvp-cd-note{font-family:Barlow,sans-serif;font-size:12px;color:#888;margin:0}'
    ].join('');
    document.head.appendChild(css);

    document.getElementById('tvpCartClose').addEventListener('click', closeCart);
    document.getElementById('tvpCartOverlay').addEventListener('click', closeCart);
  }

  /* ------------------------------------------
     OPEN / CLOSE
     ------------------------------------------ */
  function openCart() {
    document.getElementById('tvpCartDrawer').classList.add('open');
    document.getElementById('tvpCartOverlay').classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    document.getElementById('tvpCartDrawer').classList.remove('open');
    document.getElementById('tvpCartOverlay').classList.remove('open');
    document.body.style.overflow = '';
  }

  /* ------------------------------------------
     UPDATE BADGE
     ------------------------------------------ */
  function updateBadge(lines) {
    var count = 0;
    lines.forEach(function(e) { count += e.node.quantity; });
    document.querySelectorAll('.cart-badge').forEach(function(b) {
      b.textContent = count;
    });
  }

  /* ------------------------------------------
     RENDER CART
     ------------------------------------------ */
  function renderCart(cart) {
    var items    = document.getElementById('tvpCartItems');
    var foot     = document.getElementById('tvpCartFoot');
    var subEl    = document.getElementById('tvpSubtotal');
    var checkout = document.getElementById('tvpCheckout');
    if (!items) return;

    var lines = cart.lines.edges;
    updateBadge(lines);

    if (!lines.length) {
      items.innerHTML = '<div class="tvp-cd-empty"><p>Your cart is empty.</p><a href="/pages/catalog" class="pill-btn pill-btn-sm" style="margin-top:16px;display:inline-block;">SHOP ALL PATCHES</a></div>';
      if (foot) foot.style.display = 'none';
      return;
    }

    var html = '';
    var subtotal = 0;

    lines.forEach(function(edge) {
      var node    = edge.node;
      var v       = node.merchandise;
      var total   = node.cost ? parseFloat(node.cost.totalAmount.amount) : parseFloat(v.price.amount) * node.quantity;
      subtotal   += total;
      var img     = v.product.featuredImage ? v.product.featuredImage.url : '';
      var variant = v.title !== 'Default Title' ? v.title : '';

      html += '<div class="tvp-cd-item">' +
        '<img class="tvp-cd-img" src="' + img + '" alt="' + v.product.title + '" />' +
        '<div class="tvp-cd-body">' +
          '<div class="tvp-cd-name">' + v.product.title + '</div>' +
          '<div class="tvp-cd-variant">' + variant + '</div>' +
          '<div class="tvp-cd-controls">' +
            '<button class="tvp-cd-qbtn tvp-minus" data-id="' + node.id + '" data-qty="' + node.quantity + '">−</button>' +
            '<span class="tvp-cd-qty">' + node.quantity + '</span>' +
            '<button class="tvp-cd-qbtn tvp-plus" data-id="' + node.id + '" data-qty="' + node.quantity + '">+</button>' +
            '<span class="tvp-cd-price">' + TVP.formatPrice(total) + '</span>' +
            '<button class="tvp-cd-remove" data-id="' + node.id + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    });

    items.innerHTML = html;
    if (subEl) subEl.textContent = TVP.formatPrice(subtotal);
    if (foot)  foot.style.display = 'block';
    if (checkout) checkout.onclick = function() { window.location.href = cart.checkoutUrl; };

    items.querySelectorAll('.tvp-minus').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var qty = parseInt(btn.getAttribute('data-qty'), 10);
        qty <= 1 ? removeLine(btn.getAttribute('data-id')) : updateLine(btn.getAttribute('data-id'), qty - 1);
      });
    });
    items.querySelectorAll('.tvp-plus').forEach(function(btn) {
      btn.addEventListener('click', function() {
        updateLine(btn.getAttribute('data-id'), parseInt(btn.getAttribute('data-qty'), 10) + 1);
      });
    });
    items.querySelectorAll('.tvp-cd-remove').forEach(function(btn) {
      btn.addEventListener('click', function() { removeLine(btn.getAttribute('data-id')); });
    });
  }

  /* ------------------------------------------
     CART OPERATIONS
     ------------------------------------------ */
  function fetchCart() {
    var id = getCartId();
    if (!id) return Promise.resolve(null);
    return TVP.api(GET_CART, { cartId: id }).then(function(d) {
      if (!d.cart) { clearCart(); return null; }
      return d.cart;
    });
  }

  function addToCart(variantId, quantity, sellingPlanId) {
    quantity = quantity || 1;
    var id   = getCartId();
    var line = { merchandiseId: variantId, quantity: quantity };
    if (sellingPlanId) { line.sellingPlanId = sellingPlanId; }
    var lines = [line];
    var p;

    if (id) {
      p = TVP.api(ADD_LINES, { cartId: id, lines: lines }).then(function(d) { return d.cartLinesAdd.cart; });
    } else {
      p = TVP.api(CREATE_CART, { lines: lines }).then(function(d) {
        setCartId(d.cartCreate.cart.id);
        return d.cartCreate.cart;
      });
    }

    return p.then(function(cart) { renderCart(cart); openCart(); return cart; });
  }

  function updateLine(lineId, qty) {
    var id = getCartId(); if (!id) return;
    TVP.api(UPDATE_LINES, { cartId: id, lines: [{ id: lineId, quantity: qty }] })
      .then(function(d) { renderCart(d.cartLinesUpdate.cart); });
  }

  function removeLine(lineId) {
    var id = getCartId(); if (!id) return;
    TVP.api(REMOVE_LINES, { cartId: id, lineIds: [lineId] })
      .then(function(d) { renderCart(d.cartLinesRemove.cart); });
  }

  /* ------------------------------------------
     INIT
     ------------------------------------------ */
  function init() {
    injectDrawer();

    document.querySelectorAll('.icon-btn-cart').forEach(function(btn) {
      btn.addEventListener('click', function(e) { e.preventDefault(); openCart(); });
    });

    fetchCart().then(function(cart) {
      if (cart && cart.lines.edges.length) { updateBadge(cart.lines.edges); renderCart(cart); }
    });
  }

  window.TVP = window.TVP || {};
  window.TVP.cart = { add: addToCart, open: openCart, close: closeCart };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();
